import { useCallback, useState } from "react";
import { normalizeRating } from "../../utils/teacher/InputRatingUtils";

/**
 * Manages the in-memory rating state for the batch rating grid.
 *
 * Key design decisions:
 * - State is a plain object keyed by `${studentKey}_${templateContentId}`
 *   where studentKey is `${groupId}-${studentId}` in GROUPS or just studentKey in HOMEROOM.
 * - Initialization REPLACES the entire state from server data (full-state strategy).
 * - Bulk rating skips VIEW-permission students entirely.
 * - `hasChanges` tracks whether unsaved edits exist.
 * - `ratingsLoaded` is false until initializeFromServer runs, and during save.
 *
 * @returns {{
 *   ratings: Object,
 *   ratingsLoaded: boolean,
 *   hasChanges: boolean,
 *   initializeFromServer: (existingRatingsData: Array) => void,
 *   setRating: (studentKey: string, templateContentId: number, rating: string) => void,
 *   setBulkRating: (templateContentId: number, rating: string|null, editableStudentKeys: string[]) => void,
 *   resetChanges: () => void,
 *   setHasChanges: (value: boolean) => void,
 *   setRatingsLoaded: (value: boolean) => void
 * }}
 */
const useRatingState = () => {
    const [ratings, setRatings] = useState({});
    const [ratingsLoaded, setRatingsLoaded] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);

    /**
     * Initializes ratings from the server response array.
     * Each entry in existingRatingsData has:
     *   { key: studentKey, contentRatings: [{ templateContentId, rating }] }
     * Replaces the entire ratings state — full-state strategy.
     */
    const initializeFromServer = useCallback((existingRatingsData) => {
        const next = {};

        (existingRatingsData ?? []).forEach((studentRating) => {
            const studentKey = studentRating.key;
            (studentRating.contentRatings ?? []).forEach((r) => {
                next[`${studentKey}_${r.templateContentId}`] = normalizeRating(r.rating);
            });
        });

        setRatings(next);
        setRatingsLoaded(true);
        setHasChanges(false);
    }, []);

    /**
     * Updates a single cell rating.
     * @param {string} studentKey - composite student key
     * @param {number} templateContentId
     * @param {string} rating
     */
    const setRating = useCallback((studentKey, templateContentId, rating) => {
        setRatings((prev) => ({ ...prev, [`${studentKey}_${templateContentId}`]: rating }));
        setHasChanges(true);
    }, []);

    /**
     * Applies a bulk rating to all editable students for a content item.
     * Only sets cells that are currently empty (or clears all if rating is null/"").
     * EXCLUDED cells are never cleared. Skips students not in editableStudentKeys.
     *
     * @param {number} templateContentId
     * @param {string|null} rating - null or "" clears editable cells except EXCLUDED
     * @param {string[]} editableStudentKeys - keys of students with canEdit=true
     */
    const setBulkRating = useCallback((templateContentId, rating, editableStudentKeys) => {
        setRatings((prev) => {
            const next = { ...prev };
            const isClear = rating === null || rating === "";

            editableStudentKeys.forEach((studentKey) => {
                const cellKey = `${studentKey}_${templateContentId}`;
                const current = prev[cellKey];
                if (current === "EXCLUDED") return;
                const isEmpty = current === null || current === undefined || current === "";

                if (isClear || isEmpty) {
                    next[cellKey] = rating;
                }
            });

            return next;
        });
        setHasChanges(true);
    }, []);

    /**
     * Resets ratings and change tracking — used when switching subjects or grades.
     */
    const resetChanges = useCallback(() => {
        setRatings({});
        setRatingsLoaded(false);
        setHasChanges(false);
    }, []);

    return {
        ratings,
        ratingsLoaded,
        hasChanges,
        initializeFromServer,
        setRating,
        setBulkRating,
        resetChanges,
        setHasChanges,
        setRatingsLoaded
    };
};

export default useRatingState;
